import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import BlogForm, { type BlogFormErrors, type BlogFormState } from '@/components/blog/blog-form';
import { toCategoryScope } from '@/components/blog/category-scope';
import type { SidebarConfig } from '@/config/sidebarConfig';
import {
  type UpdateArticlePayload,
  getArticleApi,
  getArticleCategoriesApi,
  updateArticleApi,
  uploadArticleCoverApi,
} from '@/lib/api/blogApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { ArticleStatus } from '@shared/enums/domain.enum';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

import DashboardLayout from '../layout/DashboardLayout';
import QueryErrorState from '../ui/QueryErrorState';

const BaseArticleSchema = z.object({
  title: z.string().min(3, 'Minimum 3 caracteres').max(200),
  slug: z.string().max(200).optional(),
  excerpt: z.string().min(10, 'Minimum 10 caracteres'),
  content: z.string().min(20, 'Le contenu est trop court'),
  categoryId: z.number().int().positive('Selectionnez une categorie'),
  status: z.enum([ArticleStatus.DRAFT, ArticleStatus.PUBLISHED]),
  readTimeMinutes: z.number().int().min(1).max(120).optional(),
  tags: z.array(z.string()).optional(),
});
const FeaturedArticleSchema = BaseArticleSchema.extend({ featured: z.boolean() });

interface ScopedBlogEditProps {
  sidebarConfig: SidebarConfig;
  listRoute: string;
  /**
   * Blog edite (Lot A bis) : { clubId } ou { organizationId }.
   * Absent = blog plateforme BoulesConnect. Symetrique de ScopedBlogCreate :
   * le scope vient de la vue appelante, pas de l'article, pour que la liste
   * des categories soit correcte des le premier rendu.
   */
  scopePayload?: Record<string, number>;
  showCategoryManager?: boolean;
  showFeatured?: boolean;
}

export default function ScopedBlogEdit({
  sidebarConfig,
  listRoute,
  scopePayload,
  showCategoryManager = false,
  showFeatured = false,
}: ScopedBlogEditProps) {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const [errors, setErrors] = useState<BlogFormErrors>({});
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  const {
    data: article,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.blog.article(articleId),
    queryFn: () => getArticleApi(articleId),
    enabled: isAuthenticated && !isNaN(articleId),
  });

  // Lot A bis : meme regle qu'a la creation — on ne propose que la taxonomie
  // du blog courant, jamais celle de BoulesConnect.
  const categoryScope = toCategoryScope(scopePayload);
  const canManageCategories = showCategoryManager || categoryScope !== undefined;

  const { data: categories } = useQuery({
    queryKey: queryKeys.blog.categories(categoryScope),
    queryFn: () => getArticleCategoriesApi(categoryScope),
    enabled: isAuthenticated,
  });

  const existingCoverUrl = useMemo(() => {
    const cover = article?.files?.find(f => f.usage === 'ARTICLE_COVER');
    if (!cover) return null;
    const imagesUrl = import.meta.env.VITE_IMAGES_URL as string | undefined;
    if (imagesUrl) return `${imagesUrl}/${cover.key.replace('./uploads/', '')}`;
    const apiUrl = import.meta.env.VITE_URL_API as string;
    return `${apiUrl}/v1/files/${cover.id}/serve`;
  }, [article]);

  const resolvedCoverPreview = coverFile ? coverPreview : (coverPreview ?? existingCoverUrl);

  const initialForm = useMemo<BlogFormState | null>(() => {
    if (!article) return null;
    return {
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      content: article.content,
      categoryId: article.categoryId,
      status: article.status,
      featured: article.featured ?? false,
      readTimeMinutes: article.readTimeMinutes?.toString() ?? '',
      tagsInput: article.tags?.join(', ') ?? '',
    };
  }, [article]);

  const [form, setForm] = useState<BlogFormState | null>(null);
  const effectiveForm = form ?? initialForm;

  function validate(): UpdateArticlePayload | null {
    if (!effectiveForm) return null;
    const payload = {
      title: effectiveForm.title.trim(),
      slug: effectiveForm.slug.trim() || undefined,
      excerpt: effectiveForm.excerpt.trim(),
      content: effectiveForm.content,
      categoryId: effectiveForm.categoryId,
      status: effectiveForm.status,
      ...(showFeatured && { featured: effectiveForm.featured }),
      readTimeMinutes: effectiveForm.readTimeMinutes ? Number(effectiveForm.readTimeMinutes) : undefined,
      tags: effectiveForm.tagsInput
        ? effectiveForm.tagsInput
            .split(',')
            .map(t => t.trim())
            .filter(Boolean)
        : undefined,
    };

    const schema = showFeatured ? FeaturedArticleSchema : BaseArticleSchema;
    const result = schema.safeParse(payload);
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      const newErrors: BlogFormErrors = {};
      for (const [k, msgs] of Object.entries(fieldErrors)) {
        if (msgs?.[0]) newErrors[k as keyof BlogFormErrors] = msgs[0];
      }
      setErrors(newErrors);
      toast.error('Corrigez les erreurs du formulaire');
      return null;
    }
    setErrors({});
    return payload;
  }

  function handleCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  }

  function handleCoverRemove() {
    setCoverFile(null);
    setCoverPreview(null);
  }

  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateArticlePayload) => {
      const updated = await updateArticleApi(articleId, payload);
      if (coverFile) await uploadArticleCoverApi(articleId, coverFile);
      return updated;
    },
    onSuccess: () => {
      toast.success('Article mis a jour');
      void queryClient.invalidateQueries({ queryKey: queryKeys.blog.all() });
      navigate(listRoute);
    },
    onError: () => toast.error('Erreur lors de la mise a jour'),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = validate();
    if (payload) updateMutation.mutate(payload);
  }

  function setField<K extends keyof BlogFormState>(key: K, value: BlogFormState[K]) {
    setForm(prev => ({ ...(prev ?? initialForm!), [key]: value }));
  }

  if (isLoading || !isAuthenticated) {
    return (
      <DashboardLayout sidebarConfig={sidebarConfig}>
        <div className='flex justify-center py-20'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent' />
        </div>
      </DashboardLayout>
    );
  }

  if (isError || !article) {
    return (
      <DashboardLayout sidebarConfig={sidebarConfig}>
        <div className='p-8'>
          <QueryErrorState message='Article introuvable' onRetry={() => void refetch()} />
        </div>
      </DashboardLayout>
    );
  }

  if (!effectiveForm) return null;

  return (
    <BlogForm
      mode='edit'
      sidebarConfig={sidebarConfig}
      listRoute={listRoute}
      form={effectiveForm}
      errors={errors}
      onFieldChange={setField}
      onSubmit={handleSubmit}
      isPending={updateMutation.isPending}
      categories={categories}
      showCategoryManager={canManageCategories}
      categoryScope={categoryScope}
      showFeatured={showFeatured}
      coverPreview={resolvedCoverPreview}
      coverFileName={coverFile?.name}
      onCoverChange={handleCoverChange}
      onCoverRemove={handleCoverRemove}
      allowCoverReplace
    />
  );
}
